import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InfoGlyph } from '@upright/ui/common/generic/components/tooltip';
import { ReactNode, CSSProperties } from 'react';
import styled from 'styled-components';
import { getTextStyle } from '../../content/texts/bou-text/text-style';
import { BouColor } from '../../global/bou-colors';
import { TippyWithOriginalStyles } from './default-tippy';
import { InfoTooltip, InfoTooltipProps } from './info-tooltip';

const defaultIconColors: InfoTooltipIconColors = {
  color: BouColor.TOOL_TEXT_DARK_GRAY,
  hoverColor: BouColor.TOOL_HOVER_LIGHT_GRAY,
};

interface Props extends Partial<InfoTooltipProps> {
  content: ReactNode;
  style?: CSSProperties;
  maxWidth?: string | number | undefined;
  icon?: IconDefinition;
  inline?: boolean;
  iconSize?: SizeProp;
  iconColors?: InfoTooltipIconColors;
  iconElement?: JSX.Element;
  allowClickEventPropagation?: boolean;
  className?: string;
  darkStyle?: boolean;
}

export default function InfoTooltipWithIcon(props: Props) {
  const {
    content,
    icon,
    iconSize = '1x',
    iconColors = defaultIconColors,
    iconElement,
    style,
    allowClickEventPropagation,
    inline,
    className,
    tooltipContent,
    position = 'top',
    maxWidth,
    darkStyle,
    ...rest
  } = props;

  const inner = (
    <>
      {icon ? (
        <StyledIcon colors={iconColors} icon={icon} size={iconSize} />
      ) : (
        (iconElement ?? <InfoGlyph width="1em" />)
      )}
    </>
  );

  // for some reason the icon will not show up
  // no time to debug but leaving this
  if (darkStyle) {
    return (
      <Root inline={inline} colors={iconColors} style={style}>
        <TippyWithOriginalStyles content={<>content is here</>}>{inner}</TippyWithOriginalStyles>
      </Root>
    );
  }

  return (
    <Root
      inline={inline}
      colors={iconColors}
      style={style}
      onClick={e => {
        // by default we wish to prevent click
        // event propagation, so that when these
        // tooltips appear for example in search column
        // titles, clicking the tooltip will not trigger
        // sorting of the column
        if (!allowClickEventPropagation) {
          e.stopPropagation();
        }
      }}
      className={className}
    >
      <InfoTooltip {...{ tooltipContent: <TooltipRoot>{content}</TooltipRoot>, position, maxWidth, ...rest }}>
        {inner}
      </InfoTooltip>
    </Root>
  );
}

const Root = styled.div<IconStyleProps>`
  display: ${props => (props.inline ? 'inline-block' : 'flex')};
  align-items: center;
  color: ${props => props.colors.color};
  font-weight: normal;
  &:hover {
    color: ${props => props.colors.hoverColor};
  }

  text-transform: none;
`;

interface IconStyleProps {
  colors: InfoTooltipIconColors;
  inline?: boolean;
}

// based on https://github.com/styled-components/styled-components/issues/439#issuecomment-492779589
const StyledIcon = styled(({ paddings, colors, ...rest }) => <FontAwesomeIcon {...rest} />)<IconStyleProps>`
  padding-top: 0;
  padding-right: 0;
  padding-bottom: 0;
  padding-left: 0;
`;

interface InfoTooltipIconColors {
  color?: string;
  hoverColor?: string;
}

const TooltipRoot = styled.div`
  ${getTextStyle({ size: 'xxs' })}
  white-space: initial;
  cursor: auto;
`;
